import { Sprite, Texture, Container } from 'pixi.js';
import { loadSpriteSheetWithTransparency, loadGridSpriteSheet } from '../assets/spriteProcessor';
import { type TankSpriteConfig, type TankAnimState, WORM_SPRITE_CONFIG } from './tankSprites';

/**
 * Config-driven animated tank sprite. Renders any tank type (worm, wizard,
 * ...) from a TankSpriteConfig: it loads each declared animation (vertical
 * strip or grid sheet), falls back to the config's fallback state for missing
 * animations, and scales the art to the config's display height so all tanks
 * render at the same in-play size. Named AnimatedWormSprite for historical
 * reasons; it is the generic tank sprite.
 */
export class AnimatedWormSprite {
  container: Container;
  private sprite: Sprite;
  private textures: Map<string, Texture[]> = new Map();
  private currentAnimation: string;
  private currentFrame: number = 0;
  private frameTime: number = 0;
  private loaded: boolean = false;
  private onAnimationComplete?: () => void;
  private paused: boolean = false;
  private config: TankSpriteConfig;
  /** Scale that normalizes native art to the display height (per current
   *  animation - some sheets differ in frame size, e.g. wizard idle vs charge). */
  private baseScale = 1;
  /** Foot offset (world px) for the current animation. */
  private currentFootOffset = 18;
  private lastFacingSign: 1 | -1 = 1;

  constructor(config: TankSpriteConfig = WORM_SPRITE_CONFIG) {
    this.config = config;
    this.currentAnimation = config.fallback;
    this.container = new Container();
    this.sprite = new Sprite();
    this.sprite.anchor.set(0.5, 1);
    this.applyDimsFor(config.fallback);
    this.container.addChild(this.sprite);
  }

  /** Per-animation sizing: a sheet may override the config's nativeFrameHeight
   *  / displayHeight / footOffset (so the wizard's taller idle frame and
   *  tighter charge frame keep the cauldron the same on-screen size). */
  private applyDimsFor(state: string): void {
    const src = this.config.animations[state as TankAnimState];
    const nfh = src?.nativeFrameHeight ?? this.config.nativeFrameHeight;
    const dh = src?.displayHeight ?? this.config.displayHeight;
    this.baseScale = dh / nfh;
    this.currentFootOffset = src?.footOffsetPx ?? this.config.footOffsetPx ?? 18;
    // Re-apply the current facing with the new scale (honouring mirrorFacing).
    const s = this.config.mirrorFacing ? -this.lastFacingSign : this.lastFacingSign;
    this.sprite.scale.set(s * this.baseScale, this.baseScale);
  }

  async load(): Promise<void> {
    for (const [name, anim] of Object.entries(this.config.animations)) {
      if (!anim) continue;
      try {
        const frames = anim.grid
          ? await loadGridSpriteSheet(anim.path, {
              cols: anim.grid.cols,
              rows: anim.grid.rows,
              frameCount: anim.grid.frameCount,
              smooth: anim.smooth,
            })
          : await loadSpriteSheetWithTransparency(anim.path, anim.frameHeight ?? 60);
        this.textures.set(name, frames);
      } catch (e) {
        console.warn(`Failed to load animation ${name}:`, e);
      }
    }

    this.loaded = true;

    const startFrames = this.framesFor(this.config.fallback);
    if (startFrames && startFrames.length > 0) {
      this.sprite.texture = startFrames[0];
    } else {
      console.warn('[SPRITE] No fallback frames loaded, tank will be invisible');
    }
  }

  /** Resolve the loaded frames for a state, falling back to the config's
   *  fallback animation when that state has no own sheet (e.g. the wizard
   *  reuses idle for walk/jump/...). */
  private framesFor(state: string): Texture[] | undefined {
    const own = this.textures.get(state);
    if (own && own.length > 0) return own;
    return this.textures.get(this.config.fallback);
  }

  /** Whether this tank has its own sheet for a state (no fallback). */
  hasAnimation(state: string): boolean {
    const own = this.textures.get(state);
    return !!own && own.length > 0;
  }

  private animSourceFor(state: string): { fps: number; loop: boolean } {
    const own = this.config.animations[state as TankAnimState];
    if (own) return own;
    return this.config.animations[this.config.fallback] ?? { fps: 10, loop: true };
  }

  setAnimation(name: string, onComplete?: () => void): void {
    if (!this.loaded) return;
    if (this.currentAnimation === name) return;

    const frames = this.framesFor(name);
    if (!frames || frames.length === 0) return;

    this.currentAnimation = name;
    this.currentFrame = 0;
    this.frameTime = 0;
    this.onAnimationComplete = onComplete;
    // Sheets may differ in frame size between animations (wizard idle vs
    // charge); re-apply this animation's scale + foot offset.
    this.applyDimsFor(name);
    this.sprite.texture = frames[0];
  }

  update(deltaTime: number): void {
    if (!this.loaded || this.paused) return;

    const frames = this.framesFor(this.currentAnimation);
    if (!frames || frames.length === 0) return;

    const anim = this.animSourceFor(this.currentAnimation);
    this.frameTime += deltaTime;
    const frameDuration = 1 / anim.fps;

    if (this.frameTime >= frameDuration) {
      this.frameTime -= frameDuration;
      this.currentFrame++;

      if (this.currentFrame >= frames.length) {
        if (anim.loop) {
          this.currentFrame = 0;
        } else {
          this.currentFrame = frames.length - 1;
          this.onAnimationComplete?.();
        }
      }

      this.sprite.texture = frames[this.currentFrame];
    }
  }

  setPaused(paused: boolean): void {
    this.paused = paused;
  }

  setPosition(x: number, y: number): void {
    this.container.position.set(x, y);
  }

  /** Face left (-1) or right (+1). Keeps the display base scale; the x-mirror
   *  only flips horizontally, leaving the vertical (rotation) axis intact. */
  setFacing(sign: 1 | -1): void {
    this.lastFacingSign = sign;
    // Worm art is drawn facing LEFT (sign -1 flips it to face right). Tanks
    // whose art faces RIGHT by default set mirrorFacing to invert this.
    const s = this.config.mirrorFacing ? -sign : sign;
    this.sprite.scale.set(s * this.baseScale, this.baseScale);
  }

  /** Rotate the sprite about its feet (anchor is bottom-centre). Used to make
   *  the worm sit on a slope. Facing-independent: the x-mirror in setScale
   *  leaves the vertical axis untouched, so the same rotation works both ways. */
  setRotation(radians: number): void {
    this.sprite.rotation = radians;
  }

  setTint(color: number): void {
    this.sprite.tint = color;
  }

  setVisible(visible: boolean): void {
    this.container.visible = visible;
  }

  setAlpha(alpha: number): void {
    this.sprite.alpha = alpha;
  }

  getCurrentAnimation(): string {
    return this.currentAnimation;
  }

  /** Tanks that idle-bob continuously (e.g. the wizard) keep animating even
   *  when standing still; worms freeze on a frame when idle. */
  get idleAnimatesWhenStill(): boolean {
    return this.config.idleAnimatesWhenStill ?? false;
  }

  /** World px to drop the sprite below the worm's collision feet so the drawn
   *  feet sit on the ground (per current animation; worm art needs ~18). */
  get footOffsetPx(): number {
    return this.currentFootOffset;
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
